import { EMPTY_MARK } from '@/components/ui';
import type { VehicleHistory } from '../../../types/Vehicle';
import { formatDate } from '../../../utils/wrapDate';
import { fleetStatusLabel, formatMoney, personName } from './equipmentDisplay';

/** Money fields: History shows them as $50.00 (V3-070, FMT-16). */
const MONEY_FIELDS = new Set(['rate']);

/**
 * Audit History in plain language (EQ-25, LANG-06): field names, values and who changed them are
 * shown in words, never as a column name, enum, file name or blank. The server writes the same
 * field labels into new History comments (server/src/modules/vehicle/vehicleFieldLabels.ts).
 */
export const HISTORY_FIELD_LABELS: Record<string, string> = {
  unitNumber: 'Unit #',
  timberlineUnitNumber: 'Timberline Unit #',
  description: 'Description',
  status: 'Equipment Status',
  numberOfAxles: 'Axles',
  year: 'Year',
  rate: 'Rate',
  vehicleType: 'On-Road / Off-Road',
  roadClass: 'On-Road / Off-Road',
  vehicleClassification: 'Trip Inspection Profile',
  fuelType: 'Fuel Type',
  comments: 'Comments',
  imageUrl: 'Photo',
  currentStatus: 'Availability',
  availability: 'Availability',
  availabilityReason: 'Out-of-Service Reason',
  estimatedReturnToServiceDate: 'Expected Return',
  currentOdometer: 'Odometer',
  lastOdometerReportedType: 'Odometer Source',
  lastOdometerReportedDateTime: 'Odometer Reported',
  nextPMDue: 'Next PM Due',
  nextPMType: 'Next PM Type',
  nextMinor: 'Next Minor',
  nextMajor: 'Next Major',
  nextGreasing: 'Next Greasing',
  nextCVIP: 'Next CVIP',
  vehicleProfileId: 'Trip Inspection Profile',
  vinNumber: 'VIN',
  make: 'Make',
  model: 'Model',
  summerConfiguration: 'Summer Configuration',
  winterConfiguration: 'Winter Configuration',
  registrationNumber: 'Registration #',
  plateNumber: 'Plate',
  homeYardId: 'Home Yard',
  currentYardId: 'Current Yard',
  foremanAreaId: 'Foreman Area',
  serviceAreas: 'Service Area',
  rateMode: 'Rate Type',
  rateClassCode: 'Rate Class',
  gpsModemId: 'GPS / Skyhawk Modem',
  insurance: 'Insurance',
  defect: 'Defect'
};

export function historyFieldLabel(field?: string | null): string {
  if (!field) return EMPTY_MARK;
  return (
    HISTORY_FIELD_LABELS[field] ??
    field
      .replace(/Id$/, '')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, (c) => c.toUpperCase())
      .trim()
  );
}

const WORDS: Record<string, string> = {
  InService: 'In Service',
  OutOfService: 'Out of Service',
  'In service': 'In Service',
  'Out of service': 'Out of Service',
  ON_ROAD: 'On-Road',
  OFF_ROAD: 'Off-Road'
};

/** A History value in words: friendly status, YYYY-MM-DD dates, en dash for empty. */
export function historyValue(field: string | undefined, value: string | null | undefined): string {
  if (value == null || value.trim() === '' || value === '-' || value === 'None' || value === 'empty') return EMPTY_MARK;
  if (field === 'status') return fleetStatusLabel(value);
  if (field && MONEY_FIELDS.has(field) && Number.isFinite(Number(value))) return formatMoney(Number(value));
  if (WORDS[value]) return WORDS[value];
  if (/^\d{4}-\d{2}-\d{2}(T[\d:.]+Z)?$/.test(value)) return formatDate(value) ?? value;
  if (value === 'true') return 'Yes';
  if (value === 'false') return 'No';
  return value.replace(/ — /g, ' – ');
}

const FILE_FROM = /\s*\bfrom\s+[\w./\\-]+\.(?:csv|xlsx?|json|txt)(?:\s*\((?:[^()]|\([^()]*\))*\))?/gi;
const FILE_NAME = /\s*[\w./\\-]+\.(?:csv|xlsx?|json|txt)\b/gi;

/** "Rate changed from 50 to 55" as older entries wrote it -> "Rate changed from $50.00 to $55.00". */
const BARE_RATE_CHANGE = /^Rate changed from (empty|-?[\d.]+) to (-?[\d.]+)$/;

/**
 * File names and paths are internal; they never show in History (EQ-25). Older entries' "empty"
 * reads as the en dash and a bare rate as money (V3-070).
 */
export function historyComment(comment?: string | null): string {
  if (!comment) return '';
  const rate = comment.trim().match(BARE_RATE_CHANGE);
  if (rate) {
    const money = (v: string) => (v === 'empty' ? EMPTY_MARK : formatMoney(Number(v)));
    return `Rate changed from ${money(rate[1])} to ${money(rate[2])}`;
  }
  return comment
    .replace(/"empty"/g, EMPTY_MARK)
    .replace(FILE_FROM, '')
    .replace(FILE_NAME, '')
    .replace(/ — /g, ' – ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** Who made the change: the user, or which system for imports and integrations. */
export function historyChangedBy(
  entry: Pick<VehicleHistory, 'changedByUser' | 'external' | 'comments' | 'stage'>
): string {
  const name = personName(entry.changedByUser);
  if (name) return name;
  const text = `${entry.comments ?? ''} ${entry.stage ?? ''}`;
  if (/skyhawk/i.test(text)) return 'System – Skyhawk import';
  if (/truecontext|pm service|inspection|daily log/i.test(text)) return 'System – TrueContext';
  if (/crm/i.test(text)) return 'System – CRM import';
  return 'System – data load';
}
